import { useState, useEffect, useCallback } from 'react'

/**
 * Hook pour vérifier si une nouvelle version de l'application est disponible
 * Compare le buildTime local avec celui du serveur
 *
 * @param {number} checkInterval - Intervalle de vérification en ms (défaut: 5 minutes)
 * @returns {object} { updateAvailable, currentVersion, latestVersion, refresh }
 */
export function useVersionCheck(checkInterval = 5 * 60 * 1000) {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [currentVersion, setCurrentVersion] = useState(null)
  const [latestVersion, setLatestVersion] = useState(null)

  // Stocke le buildTime initial au premier chargement
  const [initialBuildTime, setInitialBuildTime] = useState(() => {
    return localStorage.getItem('app_build_time')
  })

  const checkForUpdates = useCallback(async () => {
    try {
      // Fetch avec cache-busting pour toujours avoir la dernière version
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store'
      })

      if (!response.ok) return

      const serverVersion = await response.json()
      setLatestVersion(serverVersion)

      // Premier chargement : on stocke le buildTime
      if (!initialBuildTime) {
        localStorage.setItem('app_build_time', serverVersion.buildTime)
        setInitialBuildTime(serverVersion.buildTime)
        setCurrentVersion(serverVersion)
        return
      }

      // Comparaison : si le buildTime serveur est différent, mise à jour dispo
      if (serverVersion.buildTime !== initialBuildTime) {
        setUpdateAvailable(true)
      }
    } catch (error) {
      console.warn('Erreur lors de la vérification de version:', error)
    }
  }, [initialBuildTime])

  // Fonction pour forcer le rechargement
  const refresh = useCallback(() => {
    // Nettoie le cache et recharge
    localStorage.setItem('app_build_time', latestVersion?.buildTime || '')
    window.location.href = '/'
  }, [latestVersion])

  useEffect(() => {
    // Vérification initiale
    checkForUpdates()

    // Vérification périodique
    const interval = setInterval(checkForUpdates, checkInterval)

    // Vérification quand l'app revient au premier plan
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdates()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [checkForUpdates, checkInterval])

  return {
    updateAvailable,
    currentVersion,
    latestVersion,
    refresh
  }
}
